import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../../assets/styles/profile/summary.scss';
import usePageMeta from '../../../services/page-meta';

function ProfileSection({section}) {
    if (!section) {
        return null;
    }

    return (
        <div className="portfolio-section">
            <h6>{section.title}</h6>
            {(section.achievements || []).map((achievement, achievementIndex) => (
                <div className="portfolio-achievement" key={`${achievement.name}-${achievementIndex}`}>
                    <div className="name">{achievement.name}</div>
                    <div>
                        <ul>
                            {(achievement.items || []).map((item, itemIndex) => (
                                <li key={`${achievement.name}-${itemIndex}`}>
                                    {item.title && <span className="title">{item.title}</span>}
                                    {item.address && <span className="address">{item.address}</span>}
                                    {(item.descriptionList || []).map((description, descriptionIndex) => (
                                        <span className="description" key={`${achievement.name}-${itemIndex}-description-${descriptionIndex}`}>
                                            {description}
                                        </span>
                                    ))}
                                    {(item.additionalInfoList || []).map((additional, additionalIndex) => (
                                        <span className="additional-info" key={`${achievement.name}-${itemIndex}-additional-${additionalIndex}`}>
                                            {additional}
                                        </span>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ProfileSummaryPage() {
    const {t} = useTranslation();
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [pdfError, setPdfError] = useState('');

    usePageMeta({
        title: data?.header?.title || t('meta.profileSummary.title', t('main.summary')),
        description: t('meta.profileSummary.description', t('app.description')),
        keywords: t('meta.profileSummary.keywords', t('app.keywords')),
    });

    useEffect(() => {
        fetch('/api/v1/profile/view', {credentials: 'same-origin'})
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Cannot load profile');
                }

                return res.json();
            })
            .then((result) => {
                setData(result);
            })
            .finally(() => {
                setLoaded(true);
            });
    }, []);

    const ensureLib = (url, globalCheck) => {
        if (globalCheck()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => reject(new Error('Cannot load PDF dependencies'));
            document.head.appendChild(script);
        });
    };

    const exportPdf = async () => {
        try {
            setPdfError('');
            await ensureLib('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', () => window.html2canvas);
            await ensureLib(
                'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
                () => window.jspdf || (window.jspdf_umd && window.jspdf_umd.jsPDF),
            );

            const {jsPDF} = window.jspdf || window.jspdf_umd || {};
            if (!jsPDF || !window.html2canvas) {
                throw new Error('Cannot initialize PDF engine');
            }

            const contentNode = document.querySelector('.profile-summary .portfolio-wrap');
            if (!contentNode) {
                throw new Error('Profile content is unavailable');
            }

            const canvas = await window.html2canvas(contentNode, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });
            const imageData = canvas.toDataURL('image/png');

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imageProps = pdf.getImageProperties(imageData);
            const imageHeight = (imageProps.height * pageWidth) / imageProps.width;

            let yOffset = 0;
            let remainingHeight = imageHeight;
            while (remainingHeight > 0) {
                pdf.addImage(imageData, 'PNG', 0, -yOffset, pageWidth, imageHeight);
                remainingHeight -= pageHeight;
                yOffset += pageHeight;
                if (remainingHeight > 0) {
                    pdf.addPage();
                }
            }

            pdf.save(`profile_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            setPdfError(error.message || 'Cannot export PDF');
        }
    };

    if (!loaded) {
        return (
            <main className="profile-summary page-container">
                <div className="profile-summary__loading">Loading...</div>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="profile-summary page-container">
                <div className="profile-summary__loading">Cannot load profile</div>
            </main>
        );
    }

    return (
        <main className="profile-summary page-container">
            <section>
                <div className="container">
                    <div className="portfolio-header flex-center">
                        <div className="primary-actions"><Link to={data.urls.main || '/main'}>{data.header.breadcrumbs}</Link></div>
                        <h1>{data.header.title}</h1>
                        <div className="secondary-actions">
                            <ul>
                                <li className="edit">
                                    <Link to="/profile/edit">{data.header.edit}</Link>
                                </li>
                                <li>
                                    <button className="btn btn-download" onClick={exportPdf}>{data.header.download}</button>
                                </li>
                            </ul>
                            {pdfError && <div className="profile-summary__error">{pdfError}</div>}
                        </div>
                    </div>

                    <div className="portfolio-wrap">
                        <div className="user-info flex-center">
                            <div className="user-avatar ">
                                <div className="user-avatar">
                                    <Link to={data.urls.edit || '/profile/edit'}><i className="icon-edit" /></Link>
                                    <div className={`photo ${data.userInfo.hasAvatar ? '' : 'not-upload'}`}>
                                        {!data.userInfo.hasAvatar && (
                                            <div className="dropzone">
                                                <Link to="/profile/edit">
                                                    <img src={data.urls.uploadIcon} title={data.labels.uploadPhoto} alt="" />
                                                    <span>{data.labels.uploadPhoto}</span>
                                                </Link>
                                            </div>
                                        )}

                                        {data.userInfo.hasAvatar && (
                                            <img className="img-fill" src={data.userInfo.avatarUrl} alt="" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="user-content">
                                <h2>
                                    {data.userInfo.hasName && data.userInfo.name}
                                    {!data.userInfo.hasName && <Link to={data.urls.edit || '/profile/edit'}>{data.labels.usernameFallback}</Link>}
                                </h2>

                                <ul>
                                    {(data.userInfo.details || []).map((detail, detailIndex) => (
                                        <li className={detail.className} key={`${detail.className}-${detailIndex}`}>
                                            {detail.className !== 'work' && detail.text}
                                            {detail.className === 'work' && (
                                                <>
                                                    <span>{detail.text}</span>
                                                    {detail.additionalText && <span className="additional-info">{detail.additionalText}</span>}
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {(data.sections || []).map((section, sectionIndex) => (
                            <ProfileSection section={section} key={`${section.title}-${sectionIndex}`} />
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
